// Imports
import { CellContext, createColumnHelper } from "@tanstack/react-table";
import { t } from "i18next";
import { CONST } from "models/constants";
import {
  formatDate,
  convertUTCToLocalTimeWithDayShift,
  shiftDowByDayDelta,
  shiftDomByDayDelta,
} from "util/datelib";
import MoreAction, { DropDownItem } from "widgets/controls/moreAction";
import Badge from "widgets/shared/badge";

// Define the structure of the JdTableModel
export class ScheduledPageTableModel {
  description: string;
  frequency: string;
  dom: number;
  dow: number;
  hour: number;
  recipients: string[];
  lastRun: string;
  _id: string;
  siteId: string;
  reportType: string;
  config: any;
  created: string;
  intergrations: any[];
  actions: string;
}

const defineScheduledTablecolumns = (
  onEditClick?: (info: CellContext<ScheduledPageTableModel, any>) => void,
  onRecipientsClick?: (info: CellContext<ScheduledPageTableModel, any>) => void,
  onRemoveClick?: (info: CellContext<ScheduledPageTableModel, any>) => void
) => {
  // Initialize the translation hook and
  const moreMenuItems: DropDownItem[] = [
    {
      label: t("button.action.devices"),
      active: true,
      onSelected: onEditClick,
      permissionRole: CONST.ROLE_MANAGER,
    },
    {
      label: t("button.action.recipients"),
      active: true,
      onSelected: onRecipientsClick,
      permissionRole: CONST.ROLE_MANAGER,
    },
    {
      label: t("button.action.remove"),
      active: true,
      onSelected: onRemoveClick,
      permissionRole: CONST.ROLE_OWNER,
    },
  ];
  // create a column helper for the JdTableModel
  const columnHelper = createColumnHelper<ScheduledPageTableModel>();

  // Define the columns for the JdTable
  const defineScheduledTablecolumns = [
    columnHelper.accessor("created", {
      enableSorting: true,
      header: () => <span className="">{t("table.header.created")}</span>,
      cell: (info) => (
        <span className="">{formatDate(info.getValue(), "lastcomms")}</span>
      ),
      meta: { rowClick: false },
    }),

    columnHelper.accessor("reportType", {
      enableSorting: false,
      header: () => <span className="">{t("table.header.report.type")}</span>,
      cell: (info) => <span className="">{info.getValue()}</span>,
      meta: { rowClick: false },
    }),

    columnHelper.accessor("description", {
      enableSorting: false,
      header: () => <span className="">{t("table.header.description")}</span>,
      cell: (info) => <span className="">{info.getValue()}</span>,
      meta: { rowClick: false },
    }),

    columnHelper.accessor("frequency", {
      enableSorting: false,
      header: () => <span className="">{t("table.header.frequency")}</span>,
      cell: (info) => (
        <span className="flex flex-row gap-1 truncate">
          <Badge
            label={info.getValue()}
            color={
              info.getValue() === "DAILY"
                ? "green"
                : info.getValue() === "WEEKLY"
                ? "blue"
                : "amber"
            }
          />
        </span>
      ),
      meta: { rowClick: false },
    }),

    columnHelper.accessor("dow", {
      enableSorting: false,
      header: () => <span className="">{t("table.header.day")}</span>,
      cell: (info) => {
        const frequency = info.row.original.frequency?.toLowerCase();
        const utcDow = info.row.original.dow;
        const utcDom = info.row.original.dom;
        const utcHour = info.row.original.hour;
        const local = convertUTCToLocalTimeWithDayShift(utcHour);
        const dayDelta = local.dayDelta;

        const dayNames = [
          "Sunday",
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
        ];

        const getOrdinalSuffix = (num: number) => {
          const j = num % 10;
          const k = num % 100;
          if (j === 1 && k !== 11) {
            return num + "st";
          }
          if (j === 2 && k !== 12) {
            return num + "nd";
          }
          if (j === 3 && k !== 13) {
            return num + "rd";
          }
          return num + "th";
        };

        const dow =
          utcDow == null ? null : shiftDowByDayDelta(Number(utcDow), dayDelta);
        const dom =
          utcDom == null ? null : shiftDomByDayDelta(Number(utcDom), dayDelta);

        if (frequency === "daily") {
          return <span className="">N/A</span>;
        } else if (frequency === "weekly") {
          return (
            <span className="">
              {dow == null ? "N/A" : dayNames[dow] || dow}
            </span>
          );
        } else if (frequency === "monthly") {
          if (dom === 32) {
            return <span className="">End of month</span>;
          }
          return dom == null ? (
            <span className="">N/A</span>
          ) : (
            <span className="">{getOrdinalSuffix(dom)}</span>
          );
        }
        return <span className="">{dow ?? dom ?? "N/A"}</span>;
      },
      meta: { rowClick: false },
    }),

    columnHelper.accessor("hour", {
      enableSorting: false,
      header: () => <span className="">{t("table.header.hour")}</span>,
      cell: (info) => {
        const utcHour = info.getValue();
        const localTime = convertUTCToLocalTimeWithDayShift(utcHour);
        return (
          <span className="">{String(localTime.hour).padStart(2, "0")}:00</span>
        );
      },
      meta: { rowClick: false },
    }),

    columnHelper.accessor("lastRun", {
      enableSorting: false,
      header: () => <span className="">{t("table.header.last.run")}</span>,
      cell: (info) => (
        <span className="">
          {formatDate(info.getValue(), "lastcomms") || "N/A"}
        </span>
      ),
      meta: { rowClick: false },
    }),

    // Actions column
    columnHelper.accessor("actions", {
      enableSorting: false,
      meta: {
        rowClick: false,
        customClass: "justify-items-center",
        customHeadClass: "justify-items-center",
      },

      header: () => <span className="">{t("table.header.actions")}</span>,
      cell: (info) => (
        <div>
          <MoreAction
            items={moreMenuItems}
            info={info}
            title={t("button.action.scheduled.options")}
            extra="w-full flex"
          />
        </div>
      ),
    }),
  ];

  return defineScheduledTablecolumns;
};

export default defineScheduledTablecolumns;
